function _slicedToArray(arr, i) { return _arrayWithHoles(arr) || _iterableToArrayLimit(arr, i) || _nonIterableRest(); }

function _nonIterableRest() { throw new TypeError("Invalid attempt to destructure non-iterable instance"); }

function _iterableToArrayLimit(arr, i) { if (!(Symbol.iterator in Object(arr) || Object.prototype.toString.call(arr) === "[object Arguments]")) { return; } var _arr = []; var _n = true; var _d = false; var _e = undefined; try { for (var _i = arr[Symbol.iterator](), _s; !(_n = (_s = _i.next()).done); _n = true) { _arr.push(_s.value); if (i && _arr.length === i) break; } } catch (err) { _d = true; _e = err; } finally { try { if (!_n && _i["return"] != null) _i["return"](); } finally { if (_d) throw _e; } } return _arr; }

function _arrayWithHoles(arr) { if (Array.isArray(arr)) return arr; }

import _extends from "@babel/runtime/helpers/esm/extends";
import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
import React, { forwardRef, useState, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import styled, { css } from 'styled-components';
import ANCHORS from '../constants/anchors';
import SIZES from '../constants/sizes';
import { SIZE_WIDTH } from './constants';
import DrawerTitle from './DrawerTitle';
import DrawerActions from './DrawerActions';
import DrawerBody from './DrawerBody';
import DrawerHeader from './DrawerHeader';
import Overlay from '../Overlay';
import Slide from '../Slide';
import { isFunction, useEventListener, trapFocus } from '../helper';
var Root = styled.div.withConfig({
  displayName: "Drawer__Root",
  componentId: "il547c-0"
})(["z-index:var(--zindex-4);visibility:", ";", ""], function (props) {
  return props.visible ? 'visible' : 'hidden';
}, function (_ref2) {
  var scrim = _ref2.scrim;
  return scrim && css(["position:fixed;right:0px;bottom:0px;top:0px;left:0px;"]);
});

var getCSSMotionDuration = function getCSSMotionDuration(open, size) {
  switch (size) {
    case SIZES.sm:
      return open ? Slide.durations.normal : Slide.durations.fast;

    case SIZES.lg:
      return open ? Slide.durations.slow : Slide.durations.slow;

    default:
      return open ? Slide.durations.slow : Slide.durations.normal;
  }
};

var Surface = styled.div.withConfig({
  displayName: "Drawer__Surface",
  componentId: "il547c-1"
})(["top:0;flex:1 0 auto;height:100%;display:flex;outline:0;z-index:var(--zindex-3);position:fixed;overflow-y:auto;flex-direction:column;background-color:var(--color-white);", " ", " ", ""], function (props) {
  return props.visible && css(["box-shadow:var(--shadow-xl);"]);
}, function (_ref3) {
  var anchor = _ref3.anchor;

  switch (anchor) {
    case ANCHORS.left:
      return css(["left:0;"]);

    case ANCHORS.right:
      return css(["right:0;"]);

    default:
      throw new Error("Undefined anchor: ".concat(anchor));
  }
}, function (_ref4) {
  var size = _ref4.size;
  var width = SIZE_WIDTH[size];

  if (!width) {
    throw new Error("Undefined size: ".concat(size));
  }

  return css(["width:", "px;"], width);
});
var Drawer =
/*#__PURE__*/
forwardRef(function (_ref, ref) {
  var children = _ref.children,
      onClose = _ref.onClose,
      open = _ref.open,
      size = _ref.size,
      anchor = _ref.anchor,
      scrim = _ref.scrim,
      rest = _objectWithoutPropertiesLoose(_ref, ["children", "onClose", "open", "size", "anchor", "scrim"]);

  var drawerRef = useRef();

  var _useState = useState(open),
      _useState2 = _slicedToArray(_useState, 2),
      visible = _useState2[0],
      setVisible = _useState2[1];

  var duration = getCSSMotionDuration(open, size);

  var handleClose = function handleClose(e) {
    return isFunction(onClose) && onClose(e);
  };

  var width = SIZE_WIDTH[size];
  var from = anchor === ANCHORS.left ? width * -1 : width;

  var handleSlideStart = function handleSlideStart() {
    open && setVisible(true);
  };

  var handleSlideEnd = function handleSlideEnd() {
    !open && setVisible(false);
  };

  var handleTab = function handleTab(event) {
    return visible && event.keyCode === 9 && trapFocus(event, drawerRef.current);
  };

  useEventListener('keydown', function (event) {
    return handleTab(event);
  }); // watch if drawer is open and set focus to it

  useEffect(function () {
    visible && drawerRef.current && drawerRef.current.focus();
  }, [visible]);
  return (
    /*#__PURE__*/
    React.createElement(Root, _extends({
      ref: ref,
      role: "presentation",
      scrim: scrim,
      visible: visible
    }, rest), scrim &&
    /*#__PURE__*/
    React.createElement(Overlay, {
      keepMounted: true,
      open: open,
      size: size,
      onClick: handleClose,
      "aria-hidden": true
    }),
    /*#__PURE__*/
    React.createElement(Slide, {
      from: from,
      "in": open,
      duration: duration,
      onStart: handleSlideStart,
      onEnd: handleSlideEnd,
      ref: drawerRef,
      tabIndex: "0",
      hiddenVisibility: true
    },
    /*#__PURE__*/
    React.createElement(Surface, {
      visible: visible,
      size: size,
      open: open,
      anchor: anchor
    }, children)))
  );
});
Drawer.Title = DrawerTitle;
Drawer.Actions = DrawerActions;
Drawer.Body = DrawerBody;
Drawer.Header = DrawerHeader;
Drawer.anchors = {
  left: ANCHORS.left,
  right: ANCHORS.right
};
Drawer.sizes = {
  sm: SIZES.sm,
  md: SIZES.md,
  lg: SIZES.lg
};
Drawer.defaultProps = {
  onClose: undefined,
  anchor: Drawer.anchors.left,
  size: Drawer.sizes.md,
  open: false,
  scrim: false
};
process.env.NODE_ENV !== "production" ? Drawer.propTypes = {
  /**
   * Gives the drawer an accessible name by referring to the element that provides the drawer title.
   */
  'aria-describedby': PropTypes.string,

  /**
   * Gives the drawer an accessible description by referring to the drawer content that describes the primary message or purpose.
   */
  'aria-labelledby': PropTypes.string,

  /**
   * Callback fired when the component requests to be closed.
   */
  onClose: PropTypes.func,

  /**
   * Side from which the drawer will appear.
   */
  anchor: PropTypes.oneOf([Drawer.anchors.left, Drawer.anchors.right]),

  /**
   * The size of the drawer.
   */
  size: PropTypes.oneOf([Drawer.sizes.sm, Drawer.sizes.md, Drawer.sizes.lg]),

  /**
   * Controls whether the drawer is open.
   */
  open: PropTypes.bool,

  /**
   * Modal navigation drawers use a scrim to block interaction with the rest of the app.
   * The scrim is placed directly below the drawer’s sheet and can be tapped or clicked to dismiss the drawer.
   */
  scrim: PropTypes.bool
} : void 0;
export default Drawer;